import { connect } from 'react-redux';
import { goBack, push } from 'redux-first-history';
import { parseNumber, parsePhoneNumber } from 'libphonenumber-js';
import { cleanPhoneNumber } from '@common/utils/numbersUtils';
import TaxVerificationPage from './component';
import { getIdVerificationInfo, getLandlordStatus } from '@rental-manager/actions/requests';
import {
  LandlordPhoneVerificationWebApiHandler_isVerified,
  PhoneVerificationSource,
} from '@zg-rentals/ts-rent-payment-client';
import { getPaymentErrorInfo } from '../../../../utils/getPaymentErrorInfo';
import { saveIdentityVerificationInfo } from '@rental-manager/actions/OnboardingActions';
import { DEFAULT_ERROR, ID_VERIFICATION_UPDATED } from '@rental-manager/constants/NotificationConstants';
import { closeNotification, showModalNotification } from '@common/actions/NotificationActions';
import { checkRequestActionSuccess } from '@common/paymentsUtils/api';
import { trackValidationError } from '@common/actions/TrackingActions';
import HTTP_STATUS from '@common/constants/HttpConstants';
import paths from '@rental-manager/pathsManifest';
import { LANDLORD_STATUS } from '@rental-manager/constants/OnboardingConstants';
import { isHighRiskOTP } from '@common/paymentsUtils/moveInCostsUtil';
import { logError } from '@zg-rentals/log-error';
import type { IdentityVerificationInfo } from '@zg-rentals/ts-rent-payment-client';

const mapStateToProps = (state, { propertyId, listing, emergencyMode }) => {
  const { identityVerificationInfo, moveInCostsSettings, paymentSettings, tenantInvitationInfo } =
    state.payments.landlord.onboarding[propertyId];
  const isHighRiskOTPPresent = isHighRiskOTP(moveInCostsSettings);
  const { userConfig } = state.payments.landlord.property;
  const phoneNumber = parseNumber(identityVerificationInfo.phoneNumber, 'US');
  return {
    identityVerificationInfo,
    paymentSettings,
    tenantInvitationInfo,
    phoneNumber: phoneNumber && 'phone' in phoneNumber ? phoneNumber.phone : '',
    verifiedPhoneNumber: listing.contactPhone,
    emergencyMode,
    config: state.payments.landlord.config,
    landlordBirthDayMax: userConfig.landlordBirthDayMax,
    landlordBirthDayMin: userConfig.landlordBirthDayMin,
    isHighRiskOTPPresent,
    user: state.user,
    currentProperty: state.payments.landlord.property.listingDetails,
  };
};

export const mapDispatchToProps = (dispatch, ownProps) => {
  const { baseUrl, propertyId, landlordStatus } = ownProps;
  return {
    getEmergencyVerificationPayload: (identityVerificationInfo) => {
      dispatch(closeNotification());

      let formattedPhoneNumber;
      try {
        // Parse saved phone number into the format expected by the backend: eg. "+11234567890"
        formattedPhoneNumber = parsePhoneNumber(identityVerificationInfo.phoneNumber, 'US').number;
      } catch {
        logError({
          error: new Error(
            `Unable to parse phone number: ${identityVerificationInfo.phoneNumber} for property ${propertyId}`,
          ),
        });
      }

      const businessStructure =
        identityVerificationInfo.selectedTaxVerificationType === 'single_member_llc' ||
        identityVerificationInfo.selectedTaxVerificationType === 'multi_member_llc'
          ? identityVerificationInfo.selectedTaxVerificationType
          : '';

      const businessType =
        identityVerificationInfo.selectedTaxVerificationType === 'individual' ? 'individual' : 'company';

      const raw = identityVerificationInfo as Record<string, unknown>;
      const ssnVal = String(raw.ssn ?? '');

      const identityPayload: IdentityVerificationInfo = {
        address: String(raw.address ?? ''),
        businessType,
        city: String(raw.city ?? ''),
        companyAddress: String(raw.companyAddress ?? ''),
        companyCity: String(raw.companyCity ?? ''),
        companyDbaName: String(raw.companyDbaName ?? ''),
        companyName: String(raw.companyName ?? ''),
        companyState: String(raw.companyState ?? ''),
        companyStructure: businessStructure,
        companyTaxId: String(raw.companyTaxId ?? ''),
        companyZip: String(raw.companyZip ?? ''),
        dobDay: parseInt(String(raw.dobDay), 10) || 0,
        dobMonth: parseInt(String(raw.dobMonth), 10) || 0,
        dobYear: parseInt(String(raw.dobYear), 10) || 0,
        firstName: String(raw.firstName ?? ''),
        lastName: String(raw.lastName ?? ''),
        phoneNumber: formattedPhoneNumber ?? '',
        ssn: ssnVal,
        ssn4: ssnVal.slice(-4),
        state: String(raw.state ?? ''),
        zip: String(raw.zip ?? ''),
      };

      return {
        propertyId,
        identityVerificationInfo: identityPayload,
      };
    },

    onSubmit: async (identityVerificationInfo, editMode: boolean, isHighRiskOTPPresent: boolean) => {
      try {
        await dispatch(closeNotification());
        await dispatch(saveIdentityVerificationInfo({ propertyId, identityVerificationInfo }));

        // check if phone verification needed, if required redirect to phone verification screen
        const isPhoneVerifiedResponse = await LandlordPhoneVerificationWebApiHandler_isVerified({
          phone: cleanPhoneNumber(identityVerificationInfo.phoneNumber),
          verificationSource: PhoneVerificationSource.OTP_RENT_PAYMENT,
        });

        if (!isPhoneVerifiedResponse.isSessionValid || !isPhoneVerifiedResponse.isVerified) {
          return dispatch(push(baseUrl + paths.onboarding.phoneVerificationPage));
        }

        // else, check if not external account set, redirect to account info screen
        if (landlordStatus === LANDLORD_STATUS.NOT_STARTED || landlordStatus === LANDLORD_STATUS.ONBOARDING_STARTED) {
          return dispatch(push(baseUrl + paths.onboarding.accountInfoPage));
        }

        // case where Irene added payment method and abondoned/exit the flow
        // started again, this time no accountInfo page as externalAccount is already set
        // but if high risk OTP requested during MIC flow, we want to handle experian logic.
        const landlordStatusResponse = await dispatch(getLandlordStatus({ propertyId }));
        await checkRequestActionSuccess(landlordStatusResponse);
        const isHighRiskOtpApproved = landlordStatusResponse.payload?.data?.isHighRiskOtpApproved;
        if (isHighRiskOTPPresent && !isHighRiskOtpApproved) {
          return dispatch(push(baseUrl + paths.onboarding.knowledgeBasedAuthenticationPage));
        }

        if (editMode) {
          await dispatch(showModalNotification(ID_VERIFICATION_UPDATED));
        }

        // else, redirect to next onboarding page
        return dispatch(
          push({
            pathname: baseUrl + paths.onboarding.confirmationPage,
            state: {
              fromWhere: paths.onboarding.taxVerificationPage,
            },
          }),
        );
      } catch (e) {
        const { httpStatus, errorCode } = getPaymentErrorInfo(e);
        const isPhoneInvalid =
          errorCode === 'phone-invalid' &&
          (httpStatus === HTTP_STATUS.SC_BAD_REQUEST || httpStatus === HTTP_STATUS.SC_FORBIDDEN);
        if (isPhoneInvalid) {
          return { error: true, phoneNumberInvalid: true };
        }
        logError({ error: e });
        dispatch(showModalNotification(DEFAULT_ERROR, { subject: 'Tax information' }));
        return { error: true };
      }
    },
    getIdVerificationInfo: async () => {
      try {
        await dispatch(closeNotification());
        return await dispatch(getIdVerificationInfo({ propertyId }));
      } catch (err) {
        logError({ error: err, context: 'getIdVerificationInfo' });
      }
    },
    onBack: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(goBack())),
    saveTaxVerificationInfo: (identityVerificationInfo, selectedOption) =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() =>
          dispatch(
            saveIdentityVerificationInfo({
              propertyId,
              identityVerificationInfo: {
                idPageAccessed: true,
                selectedTaxVerificationType: selectedOption,
                ...identityVerificationInfo,
              },
            }),
          ),
        ),
    onCancel: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(push(baseUrl + paths.onboarding.confirmationPage))),
    onClose: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(push(baseUrl + paths.onboarding.exitPaymentSetup))),
    trackValidationError: (data) => dispatch(trackValidationError(data)),
  };
};

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  onSubmit: ({ identityVerificationInfo = {} }) =>
    dispatchProps.onSubmit(
      identityVerificationInfo,
      stateProps.identityVerificationInfo.editMode,
      stateProps.isHighRiskOTPPresent,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(TaxVerificationPage);
